import { useState, useEffect, useRef } from 'react';
import { team } from '@/mocks/data';

export default function AboutSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const lawyer1 = team[0];
  const lawyer2 = team[1];

  return (
    <section id="about" ref={ref} className="relative py-24 md:py-32 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className={`text-center mb-16 md:mb-20 transition-all duration-800 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-4 py-1.5 rounded-full border border-accent-400 text-xs font-semibold tracking-[0.15em] uppercase text-accent-700 mb-4 hover:border-primary-500 hover:text-primary-500 hover:bg-primary-50 transition-all duration-300 cursor-default">
            Sobre nosotros
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-[1.15]">
            Quiénes somos
          </h2>
        </div>

        {/* Main content */}
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start transition-all duration-800 delay-150 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* Text */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="space-y-6">
              <div className="relative pl-5 md:pl-6">
                <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-accent-500" />
                <p className="text-base md:text-lg text-foreground-700 leading-[1.8]">
                  En cada caso se ofrece un abordaje personalizado, entendiendo que toda situación legal requiere análisis, responsabilidad y un acompañamiento acorde a sus particularidades.
                </p>
                <p className="text-base md:text-lg text-foreground-700 leading-[1.8] mt-6">
                  El estudio trabaja con compromiso, cercanía y profesionalismo, brindando respuestas claras y asesoramiento jurídico serio durante cada etapa del proceso.
                </p>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-shield-check-line text-lg text-accent-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">Compromiso garantizado</p>
                  <p className="text-xs text-foreground-500 mt-0.5">Atención personalizada en cada caso</p>
                </div>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="flex gap-4 md:gap-6 justify-center lg:justify-end">
              {/* Lawyer 1 */}
              <div className="w-[48%] md:w-[44%]">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={lawyer1.image}
                    alt={lawyer1.name}
                    className="w-full h-[340px] md:h-[420px] object-cover object-top"
                  />
                </div>
                <div className="mt-4 text-center lg:text-left">
                  <p className="font-heading text-base md:text-lg font-bold text-foreground-950">E. Joel Gou</p>
                  <p className="text-sm text-foreground-600 font-medium">Abogado</p>
                  <p className="text-sm text-foreground-500">{lawyer1.role}</p>
                </div>
              </div>

              {/* Lawyer 2 - offset downward */}
              <div className="w-[48%] md:w-[44%] mt-10 md:mt-14">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={lawyer2.image}
                    alt={lawyer2.name}
                    className="w-full h-[340px] md:h-[420px] object-cover object-top"
                  />
                </div>
                <div className="mt-4 text-center lg:text-left">
                  <p className="font-heading text-base md:text-lg font-bold text-foreground-950">Valentina Amigo</p>
                  <p className="text-sm text-foreground-600 font-medium">Abogada</p>
                  <p className="text-sm text-foreground-500">{lawyer2.role}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave bottom - transition to Testimonials (background-50) */}
      <div className="absolute bottom-0 left-0 w-full pointer-events-none overflow-hidden"
        style={{ height: '60px', marginBottom: '-1px' }}
      >
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-full block"
          style={{ display: 'block' }}
        >
          <path d="M0 60H1440V30C1080 12 360 48 0 30V60Z" fill="oklch(0.94 0.008 95)" stroke="none" />
        </svg>
      </div>
    </section>
  );
}